import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import TwoFA from "Login/TwoFA";
import API from "Signups/API";
import { useGetSignupListType } from "Signups/SignupView/utils/useGetSignupListType";
import { useSignups } from "Signups/state.js";
import { motion } from "framer-motion";

import { colors } from "UI/colors";

import { validateEmail } from "Utilities/validateEmail";

NewUserForm.propTypes = {
  handleCloseClick: PropTypes.func,
  onFormComplete: PropTypes.func,
  headerText: PropTypes.string,
  defaultContactInfo: PropTypes.string,
};

export default function NewUserForm({
  handleCloseClick,
  onFormComplete,
  headerText = "Great! First, let's get your information:",
  defaultContactInfo = "",
}) {
  const [state, dispatch] = useSignups();
  const [name, setName] = useState(state.anonymousUserClearedData?.name || "");
  const [emailOrPhone, setEmailOrPhone] = useState(
    state.anonymousUserClearedData?.emailOrPhone || ""
  );
  const [validationError, setValidationError] = useState(null);
  const signupListID = state.currentSignupList.ID;

  useEffect(() => {
    setValidationError(null);
  }, [name, emailOrPhone]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!name) return setValidationError("Please fill out your name.");
    if (!emailOrPhone)
      return setValidationError("Please enter an email or phone.");

    if (emailOrPhone.includes("@")) {
      const isValid = validateEmail(emailOrPhone);
      if (!isValid) {
        return setValidationError("Please enter a valid email or phone.");
      }
    }

    const newUser = {
      name,
      emailOrPhone,
    };

    dispatch({
      type: "user",
      payload: {
        ...newUser,
        isAnonymousUser: true,
      },
    });

    if (onFormComplete) {
      onFormComplete(newUser);
    }
  };

  const { isPollType } = useGetSignupListType();

  const handleAuthSuccess = ({ userID, flexibleInputVal }) => {
    API.getSignupListInfo(signupListID)
      .post()
      .then((resp) => {
        dispatch({
          type: "Set Current Signup List",
          triggerRespondentRefresh: true,
          ...resp,
        });
        if (isPollType) {
          dispatch({
            type: "expandedItemID",
            ID: null,
          });
        }
        onFormComplete &&
          onFormComplete({ ID: userID, emailOrPhone: flexibleInputVal });
      })
      .catch((err) => {
        setValidationError(
          "Something went wrong. Please refresh and try again. If the problem persists, contact help@flocknote.com"
        );
      });
  };

  return (
    <Wrapper>
      <CloseButton onClick={handleCloseClick}>
        <FontAwesomeIcon icon={faTimesCircle} />
      </CloseButton>
      <Header>{headerText}</Header>
      <TwoFA
        variant="signupList"
        onSuccess={handleAuthSuccess}
        emailPhone={defaultContactInfo}
        headerText={false}
        showNetwork={false}
        width="100%"
        animateEntranceHeight
        stoutInputs
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0.08);
  padding: 15px;
  margin: 0 -15px -10px;
  border-radius: 8px;
`;

const CloseButton = styled.div`
  color: #6a6a6a;
  position: absolute;
  top: 10px;
  right: 15px;
  font-size: 21px;
  z-index: 100;
  cursor: pointer;
  transition: color 150ms;

  &:hover {
    color: ${colors.fnBlue};
  }

  &:active {
    transform: scale(0.97);
  }
`;

const Header = styled.div`
  color: #1e1e1e;
  margin-bottom: 18px;
  padding-right: 24px;
  font-weight: 400;
  opacity: 0.8;
`;

const NameField = styled.div`
  margin-bottom: 14px;
`;

const ButtonWrapper = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 400px) {
    flex-direction: column;
  }
`;

const Error = styled(motion.div)`
  margin-top: 3px;
  text-align: right;
  color: #f0243a;
  font-size: 15px;
  font-weight: 400;
`;

const LoginWrapper = styled.div`
  color: #585858;
  font-weight: 400;
  font-size: 15px;

  a {
    text-decoration: underline !important;
  }

  a:hover {
    color: ${colors.fnBlue} !important;
  }

  @media (max-width: 400px) {
    margin-bottom: 10px;
    font-size: 14px;
  }
`;
